/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useRef, useState } from 'react';
import DatePicker from 'rsuite/DatePicker';

type DateTimePickerType = {
  value: any;
  onChange: (e: any) => void;
  onKeyDown?: (e: any) => void;
  handleClose?: () => void;
  placeholder?: string;
  disabled?: boolean;
  container?: any;
  customDatePicker?: boolean;
  minDate?: Date;
};

function startOfDay(date: Date): Date {
  const d = new Date(date);
  d.setHours(0, 0, 0, 0);
  return d;
}

function startOfMinute(date: Date): Date {
  const d = new Date(date);
  d.setSeconds(0, 0);
  return d;
}

function isSameDay(a: Date, b: Date): boolean {
  return startOfDay(a).getTime() === startOfDay(b).getTime();
}

type Segment = 'month' | 'day' | 'year' | 'hour' | 'minute';

//segment under the cursor for the format "MM/dd/yyyy HH:mm"
function getSegmentAt(index: number | null): Segment | null {
  if (index === null) return null;
  if (index <= 2) return 'month';
  if (index <= 5) return 'day';
  if (index <= 10) return 'year';
  if (index <= 13) return 'hour';
  return 'minute';
}

function getSegmentValue(date: Date, segment: Segment): number {
  switch (segment) {
    case 'month':
      return date.getMonth() + 1;
    case 'day':
      return date.getDate();
    case 'year':
      return date.getFullYear();
    case 'hour':
      return date.getHours();
    default:
      return date.getMinutes();
  }
}

//same rule rsuite uses to move to the next segment: all digits typed,
//or the first digit cannot be followed by another (e.g. month 2)
function isSegmentComplete(segment: Segment, value: number, digits: number) {
  switch (segment) {
    case 'year':
      return digits >= 4;
    case 'month':
      return digits >= 2 || value * 10 > 12;
    case 'day':
      return digits >= 2 || value * 10 > 31;
    case 'hour':
      return digits >= 2 || value * 10 > 23;
    default:
      return digits >= 2 || value * 10 > 59;
  }
}

function DateTimePicker({
  value,
  onChange,
  handleClose,
  placeholder,
  disabled,
  container,
  onKeyDown,
  customDatePicker,
  minDate,
}: DateTimePickerType) {
  //to close date picker on scroll
  const datePickerRef = useRef<any>();

  const parsedValue = React.useMemo(() => {
    if (!value) return null;

    if (value instanceof Date) return value;

    const date = new Date(value);

    return isNaN(date.getTime()) ? null : date;
  }, [value]);

  const shouldDisableCalendarDate = (date: Date) => {
    if (!minDate) return false;
    return date < startOfDay(minDate);
  };

  const shouldDisableHour = (hour: number, date: Date) => {
    if (!minDate || !isSameDay(date, minDate)) return false;
    return hour < minDate.getHours();
  };

  const shouldDisableMinute = (minute: number, date: Date) => {
    if (!minDate || !isSameDay(date, minDate)) return false;
    return (
      date.getHours() < minDate.getHours() ||
      (date.getHours() === minDate.getHours() &&
        minute < minDate.getMinutes())
    );
  };

  //compares date and time (to the minute) since the input shows HH:mm
  const isBeforeMinDate = (date: Date) => {
    if (!minDate) return false;
    return startOfMinute(date) < startOfMinute(minDate);
  };

  //value shown in the input while typing. rsuite keeps each typed segment
  //internally, so the shown value must follow every keystroke to stay in sync
  const [draftValue, setDraftValue] = useState<Date | null>(parsedValue);

  useEffect(() => {
    setDraftValue(parsedValue);
  }, [parsedValue]);

  //a new Date instance makes rsuite reset its typed segments to this value
  const revertToValue = () => {
    setDraftValue(parsedValue ? new Date(parsedValue) : null);
  };

  //rsuite sends the year as typed so far (2 -> 20 -> 202 -> 2027)
  const isIncompleteDate = (date: Date) =>
    isNaN(date.getTime()) || date.getFullYear() < 1000;

  //digits typed so far in the current segment
  const typingRef = useRef<{ segment: Segment | null; digits: number }>({
    segment: null,
    digits: 0,
  });

  const trackTyping = (e: React.KeyboardEvent) => {
    const input = e.target as HTMLInputElement;
    if (/^\d$/.test(e.key) && input?.tagName === 'INPUT') {
      const segment = getSegmentAt(input.selectionStart);
      const typing = typingRef.current;
      typingRef.current =
        typing.segment === segment
          ? { segment, digits: typing.digits + 1 }
          : { segment, digits: 1 };
    } else {
      typingRef.current = { segment: null, digits: 0 };
    }
  };

  const isSegmentBeingTyped = (date: Date) => {
    const { segment, digits } = typingRef.current;
    if (!segment || digits === 0) return false;
    return !isSegmentComplete(
      segment,
      getSegmentValue(date, segment),
      digits,
    );
  };

  const isSameAsValue = (date: Date) =>
    Boolean(
      parsedValue &&
        startOfMinute(date).getTime() === startOfMinute(parsedValue).getTime(),
    );

  const handleChange = (date: Date | null) => {
    if (!date) {
      setDraftValue(null);
      onChange(null);
      return;
    }
    if (isIncompleteDate(date)) {
      setDraftValue(date);
      return;
    }
    if (isBeforeMinDate(date)) {
      //a segment still being typed can pass through a past value (month 1 -> 11)
      if (isSegmentBeingTyped(date)) setDraftValue(date);
      //block a completed date/time earlier than minDate
      else revertToValue();
      return;
    }
    setDraftValue(date);
    onChange(date);
  };

  useEffect(() => {
    const handleScroll = () => {
      if (container && container?.current && datePickerRef?.current) {
        datePickerRef?.current.close();
      }
    };

    if (container && container?.current) {
      container?.current.addEventListener('scroll', handleScroll);
    }

    return () => {
      if (container && container?.current) {
        container?.current.removeEventListener('scroll', handleScroll);
      }
    };
  }, []);

  return (
    <div
      className="relative [&>*]:w-full"
      onKeyDownCapture={trackTyping}
      onKeyDown={e => onKeyDown && onKeyDown(e)}
    >
      <DatePicker
        onChange={handleChange}
        onBlur={() => {
          typingRef.current = { segment: null, digits: 0 };
          //discard an unfinished or past entry that was not saved
          if (
            draftValue &&
            !isSameAsValue(draftValue) &&
            (isIncompleteDate(draftValue) || isBeforeMinDate(draftValue))
          )
            revertToValue();
        }}
        value={draftValue}
        format="MM/dd/yyyy HH:mm"
        shouldDisableDate={shouldDisableCalendarDate}
        shouldDisableHour={shouldDisableHour}
        shouldDisableMinute={shouldDisableMinute}
        onClose={() => {
          handleClose && handleClose();
        }}
        disabled={disabled}
        placeholder={placeholder ?? 'mm/dd/yyyy hh:mm'}
        ref={datePickerRef}
        placement="autoVerticalStart"
        cleanable={disabled ? false : true}
        className={customDatePicker ? 'custom-date-picker' : ''}
      />
    </div>
  );
}

export { DateTimePicker };
